import { useLingui } from '@lingui/react/macro';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { toaster } from '#features/ui/components/Toaster';

import {
  joinVoiceRoom,
  kickFromVoiceRoom,
  muteInVoiceRoom,
  voiceRoomQueryKey,
  VoiceRequestError,
} from '#features/voice/model/voice';
import { toVoiceMembers } from '#features/voice/hooks/useVoiceRoom';
import { useVoiceStore } from '#features/voice/model/voiceStore';

/**
 * Действия над голосовой комнатой из интерфейса: войти, выгнать, заглушить.
 *
 * Выход живёт не здесь, а в {@link useVoiceRoom}: он обязан сперва разорвать медиа и только потом
 * сообщить серверу, иначе человек ещё несколько секунд слышен остальным после нажатия «выйти».
 */
export const useVoiceActions = () => {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const setConnecting = useVoiceStore((s) => s.setConnecting);
  const setMembers = useVoiceStore((s) => s.setMembers);
  const setError = useVoiceStore((s) => s.setError);
  const reset = useVoiceStore((s) => s.reset);
  const [pending, setPending] = useState(false);

  const join = useCallback(
    async (sessionId: string, context?: { gameName?: string | null; hostName?: string | null }) => {
      setPending(true);
      setError(null);
      try {
        const room = await joinVoiceRoom(sessionId);
        if (!room.livekitUrl || !room.token) {
          // Сервер согласился, но не выдал ключей — считать это успехом нельзя, иначе виджет
          // повиснет в «подключаюсь» навсегда.
          throw new VoiceRequestError('voice.unavailable');
        }

        setConnecting({
          sessionId: room.sessionId,
          postId: room.postId,
          hostId: room.hostId,
          livekitUrl: room.livekitUrl,
          token: room.token,
          maxParticipants: room.maxParticipants,
          gameName: context?.gameName ?? null,
          hostName: context?.hostName ?? null,
        });
        setMembers(toVoiceMembers(room.participants));
        queryClient.setQueryData(voiceRoomQueryKey(sessionId), room);
        return room;
      } catch (error) {
        const code = error instanceof VoiceRequestError ? error.errorCode : 'global.error';
        // Порядок важен: `reset` очищает и код отказа тоже, поэтому сначала гасим состояние и
        // только потом записываем причину. Наоборот — и человек, которому отказали, не увидит
        // ничего: кнопка просто перестанет нажиматься.
        reset();
        setError(code, sessionId);
        throw error;
      } finally {
        setPending(false);
      }
    },
    [queryClient, reset, setConnecting, setError, setMembers]
  );

  /**
   * Модерация обязана говорить, когда не сработала.
   *
   * <p>До 27.08 отказ здесь пропадал: вызовы уходили через `void`, и хост, нажавший «заглушить»
   * при недоступном медиасервере, видел ровно то же, что при успехе, — ничего. Человек в это
   * время продолжал говорить. Проверено на dev: трек в LiveKit оставался `muted=false`.
   */
  const moderationFailed = useCallback(() => {
    toaster.create({
      title: t`post:voice.moderationFailed`,
      description: t`post:voice.moderationFailedDetail`,
      type: 'error',
      duration: 6000,
    });
  }, [t]);

  const kick = useCallback(
    async (sessionId: string, userId: string) => {
      try {
        await kickFromVoiceRoom(sessionId, userId);
      } catch (error) {
        moderationFailed();
        throw error;
      }
      // Состав придёт кадром по вебсокету, но хост должен увидеть результат сразу же.
      await queryClient.invalidateQueries({ queryKey: voiceRoomQueryKey(sessionId) });
    },
    [moderationFailed, queryClient]
  );

  const mute = useCallback(
    async (sessionId: string, userId: string) => {
      try {
        await muteInVoiceRoom(sessionId, userId);
      } catch (error) {
        moderationFailed();
        throw error;
      }
    },
    [moderationFailed]
  );

  return { join, kick, mute, pending };
};
